import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import * as dayjs from 'dayjs';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  lastValueFrom,
  map,
  of,
  takeUntil,
  tap,
} from 'rxjs';
import { IAcceptanceData } from '../models/acceptance.data.model';
import {
  PropostaSalarialModel,
  PropostaSalarialView,
} from '../models/proposta-salarial.model';
import { RejectedReason } from '../models/rejected-reason-alert.models';
import { CO_STATUS_CONST } from '../utils/utils';
import { AuthService } from './auth.service';
import { GestorService } from './gestor.service';
import { IpAddressService } from '../shared/services/ip-address.service';
import { IRejectionData } from '../models/rejection.data.model';
import { IAcceptanceDataWithoutFlagData } from '../models/acceptance.data.without-flag.model';

@Injectable({
  providedIn: 'root',
})
export class PropostaSalarialService {
  private basePath: string;
  private dbPath: string;
  private propostaView: PropostaSalarialView;
  private currentTemplate: BehaviorSubject<any> = new BehaviorSubject(null);
  private status$: BehaviorSubject<number> = new BehaviorSubject(null);
  private templateId: string;
  onDestroy$ = new Subject();

  constructor(
    private firestore: AngularFirestore,
    private db: AngularFireDatabase,
    private authService: AuthService,
    private gestorService: GestorService,
    private ipAddressService: IpAddressService
  ) {
    this.basePath = `empresaMae/${this.authService.dominioId}`;
    this.dbPath = `${this.authService.dominioId}/carta-oferta/home-list`;
    this.propostaView = {};
  }

  getView() {
    return this.propostaView;
  }

  getStatus$() {
    return this.status$;
  }

  getTemplate() {
    return this.currentTemplate.value;
  }

  async getDados() {
    const prop = await lastValueFrom(this.getProposal());
    if (!prop.exists) {
      return {};
    }
    const proposta = prop.data() as PropostaSalarialModel;
    this.templateId = proposta.templateCartaOferta.split('/').pop();
    await this.setPropostaView(proposta, prop.ref.id);

    return proposta;
  }

  async setPropostaView(proposta: PropostaSalarialModel, id: string) {
    this.propostaView = {
      ...proposta,
      id,
      cargo: await this.getCargo(proposta.cargo),
      companyName: await this.getCompanyName(proposta.filial),
      modeloTrabalho: this.getModeloTrabalho(
        Number.parseInt(proposta.modeloTrabalho)
      ),
      ...(await this.getGrupoSalarialInfo(proposta.grupoSalarial)),
    };

    this.status$.next(proposta.status);
    this.loadGestor(proposta.gestor);
  }

  getProposal() {
    if (this.authService.isPreview) {
      return this.getProposalDraft();
    }
    return this.getCreatedProposal();
  }

  getCreatedProposal() {
    return this.firestore
      .doc(`${this.basePath}/propostasSalariais/${this.authService.userId}`)
      .get();
  }

  getProposalDraft() {
    return this.firestore
      .doc(`${this.basePath}/proposalsDraft/${this.authService.userId}`)
      .get();
  }

  async getCargo(id: string) {
    return firstValueFrom(
      this.firestore.doc(`${this.basePath}/cargos/${id}`).get()
    ).then((doc: any) => {
      return doc.exists ? doc.data().nome : null;
    });
  }

  async getGrupoSalarialInfo(
    grupoSalarialPath: string
  ): Promise<{ salaryType: 'horista' | 'mensalista'; grupoSalarialName: string }> {
    if (!grupoSalarialPath) {
      return { salaryType: 'mensalista', grupoSalarialName: null };
    }

    const grupo = await firstValueFrom(
      this.firestore.doc(grupoSalarialPath).get()
    );
    const data = grupo.data() as any;
    return {
      salaryType: data?.salaryType ?? 'mensalista',
      grupoSalarialName: data?.nome ?? null,
    };
  }

  async getCompanyName(companyId: string) {
    const company = await firstValueFrom(this.firestore.doc(companyId).get());
    return company.data()['name'];
  }

  async aceitarOferta(shouldSaveAcceptance: boolean) {
    return this.changeStatus(
      CO_STATUS_CONST.ACEITO,
      null,
      shouldSaveAcceptance
    );
  }

  async recusarOferta(reason: RejectedReason) {
    return this.changeStatus(CO_STATUS_CONST.RECUSADO, reason);
  }

  loadTemplate() {
    const path = `empresaMae/${this.authService.dominioId}/olTemplate/${this.templateId}`;
    return this.firestore
      .doc(path)
      .valueChanges()
      .pipe(
        takeUntil(this.onDestroy$),
        tap((value) => this.currentTemplate.next(value))
      );
  }

  async visualizarOferta() {
    await this.changeStatus(CO_STATUS_CONST.VISUALIZADO);
  }

  private async changeStatus(
    newStatus: number,
    reason?: RejectedReason,
    shouldSaveAcceptance?: boolean
  ) {
    const propPath = `${this.basePath}/propostasSalariais/${this.authService.userId}`;
    const finishedStatus = [CO_STATUS_CONST.ACEITO, CO_STATUS_CONST.RECUSADO];
    const removalDate = finishedStatus.includes(newStatus) ? dayjs().add(7, 'day').valueOf() : null;

    const data = {
      updatedBy: this.authService.userId,
      updatedAt: new Date(),
      status: newStatus,
      reason: reason ? reason : null,
      oldStatus: newStatus === CO_STATUS_CONST.VISUALIZADO ? CO_STATUS_CONST.ENVIADO : this.propostaView.status,
    }
    await this.firestore.collection(`${propPath}/historico`).add(data);

    if (newStatus === CO_STATUS_CONST.ACEITO) {
      if (shouldSaveAcceptance) {
        this.saveAcceptanceData();
        this.saveAcceptanceDataWithoutFlag();
      } else {
        // Sem aceite digital: salvar apenas o "sem flag"
        this.saveAcceptanceDataWithoutFlag();
      }
    }
    if (newStatus === CO_STATUS_CONST.RECUSADO) {
      this.saveRejectionData();
    }


    await this.firestore.doc(propPath).update({ status: newStatus });
    await this.firestore
      .doc(propPath)
      .update({ reason: reason ? reason : null });
    await this.addRegistroAtividade(
      this.authService.userId,
      newStatus,
      reason ? reason : null
    );
    this.db.database.ref(`${this.dbPath}/${this.authService.userId}`).update({
      status: newStatus,
      removalDate,
      reason: reason ? reason : null,
    });

    this.status$.next(newStatus);

    // todo: alterar realtime também
    this.propostaView.status = newStatus;
  }

  private getModeloTrabalho(modeloTrabalho: number) {
    switch (modeloTrabalho) {
      case 1:
        return 'Presencial';
      case 2:
        return 'Remoto';
      case 3:
        return 'Híbrido';
    }

    return '';
  }

  private loadGestor(gestorPath: string) {
    if (gestorPath) {
      this.gestorService
        .getGestorName(gestorPath)
        .subscribe((gestorName) => (this.propostaView.gestorName = gestorName));
    }
  }

  private async addRegistroAtividade(
    propostaId: string,
    reply: number,
    reason?: RejectedReason
  ) {
    const atualRegistroAtiv = (
      await this.getRegistroAtivValue(propostaId)
    ).val();
    atualRegistroAtiv.push({
      atividade:
        reply === CO_STATUS_CONST.ACEITO
          ? 'PROPOSTA_ACEITA'
          : reply === CO_STATUS_CONST.RECUSADO
            ? 'PROPOSTA_RECUSADA'
            : 'PROPOSTA_VISUALIZADA',
      feitoPor: 'candidate',
      modulo: 'CARTA_OFERTA',
      timestamp: dayjs().valueOf(),
      reason: reason ? reason : null,
    });
    return this.db.database
      .ref(`${this.dbPath}/${propostaId}`)
      .child('registroAtiv')
      .set(atualRegistroAtiv);
  }

  async getRegistroAtivValue(propostaId: string) {
    return this.db.database
      .ref(`${this.dbPath}/${propostaId}`)
      .child('registroAtiv')
      .once('value');
  }

  getProposalPdf(): Observable<string | null> {
    return this.firestore
      .doc<{ proposalPdf: string }>(
        `${this.basePath}/propostasSalariais/${this.authService.userId}`
      )
      .valueChanges()
      .pipe(map((data) => (data?.proposalPdf ? data.proposalPdf : null)));
  }

  getAcceptanceData(): Observable<IAcceptanceData | null> {
    return this.firestore
      .doc<{ acceptedAud: IAcceptanceData }>(
        `${this.basePath}/propostasSalariais/${this.authService.userId}`
      )
      .valueChanges()
      .pipe(
        map((data) =>
          data?.acceptedAud
            ? ({
              ...data.acceptedAud,
              acceptedAt: this.transformToDate(
                (data.acceptedAud as any).acceptedAt
              ),
            } as IAcceptanceData)
            : null
        )
      );
  }

  getAcceptanceDataWithoutFlag(): Observable<IAcceptanceDataWithoutFlagData | null> {
    return this.firestore
      .doc<{ acceptedAudWithoutFlag: IAcceptanceDataWithoutFlagData }>(
        `${this.basePath}/propostasSalariais/${this.authService.userId}`
      )
      .valueChanges()
      .pipe(
        map((data) =>
          data?.acceptedAudWithoutFlag
            ? ({
              ...data.acceptedAudWithoutFlag,
              acceptedAtWithoutFlag: this.transformToDate(
                (data.acceptedAudWithoutFlag as any).acceptedAtWithoutFlag
              ),
            } as IAcceptanceDataWithoutFlagData)
            : null
        )
      );
  }

  getRejectionData(): Observable<IRejectionData | null> {
    return this.firestore
      .doc<{ rejectionAud: IRejectionData }>(
        `${this.basePath}/propostasSalariais/${this.authService.userId}`
      )
      .valueChanges()
      .pipe(map((data) => data?.rejectionAud ? ({
        ...data.rejectionAud,
        rejectedAt: this.transformToDate(
          (data.rejectionAud as any).rejectedAt
        ),
      } as IRejectionData) : null));
  }

  private transformToDate(value: any): Date | null {
    return value instanceof Date
      ? value
      : value?.toDate
        ? value.toDate()
        : typeof value === 'string' || typeof value === 'number'
          ? new Date(value)
          : null;
  }

  saveAcceptanceData(): void {
    this.ipAddressService.getIpAddress().subscribe(() => {
      const acceptedData = {
        acceptedAt: new Date(),
        ipAddress: this.ipAddressService.ip,
      };

      this.firestore
        .doc(`${this.basePath}/propostasSalariais/${this.authService.userId}`)
        .update({ acceptedAud: acceptedData })
        .catch((error) => {
          console.error('Erro ao salvar dados de aceite:', error);
          return null;
        });
    });
  }

  saveAcceptanceDataWithoutFlag(): void {
    this.ipAddressService.getIpAddress().subscribe(() => {
      const acceptedDataWithoutFlag = {
        acceptedAtWithoutFlag: new Date(),
        ipAddress: this.ipAddressService.ip,
      };

      this.firestore
        .doc(`${this.basePath}/propostasSalariais/${this.authService.userId}`)
        .update({ acceptedAudWithoutFlag: acceptedDataWithoutFlag })
        .catch((error) => {
          console.error('Erro ao salvar dados de aceite sem flag:', error);
          return null;
        });
    });
  }

  saveRejectionData(): void {
    this.ipAddressService.getIpAddress().subscribe(() => {
      const rejectionData = {
        rejectedAt: new Date(),
        ipAddress: this.ipAddressService.ip,
      };

      this.firestore
        .doc(`${this.basePath}/propostasSalariais/${this.authService.userId}`)
        .update({ rejectionAud: rejectionData })
        .catch((error) => {
          console.error('Erro ao salvar dados de recusa:', error);
          return null;
        });
    });
  }

  saveProposalPdf(path?: string): void {
    const domainId = this.authService.dominioId;
    const candidateId = this.propostaView?.id || this.authService.userId;
    const currentStatus = Number(
      this.status$.value ?? this.propostaView.status
    );
    const isRejected = currentStatus === CO_STATUS_CONST.RECUSADO;
    const folder = isRejected ? 'rejected_proposals' : 'accepted_proposals';
    const finalPath = path ?? `${domainId}/${folder}/${candidateId}.pdf`;

    this.firestore
      .doc(`${this.basePath}/propostasSalariais/${this.authService.userId}`)
      .update({ proposalPdf: finalPath })
      .catch((error) => {
        console.error('Erro ao salvar proposalPdf:', error);
        return null;
      });
  }
}
